'use client'

import { useMemo } from 'react'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import * as ConversationTypes from '@marketplace-web/messaging/conversation-messaging-data'
import { ConversationMessageType } from '@marketplace-web/messaging/conversation-messaging-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { BrazeInboxMessage, useBrazeInboxMessageCards } from '@marketplace-web/braze/braze-feature'

import ConversationMessage from './ConversationMessage'
import MessageTime from '../MessageTime'
import { isMessageGrouped, getConversationIdFromUrl } from '../../utils'

type Props = {
  toggleSidebar?: () => void
  openOfferModal: () => void
}

const ConversationMessages = ({ toggleSidebar, openOfferModal }: Props) => {
  const { relativeUrl } = useBrowserNavigation()
  const conversationId = getConversationIdFromUrl(relativeUrl)
  const { conversation, conversationMessages } = useConversationContext()
  const { user } = useSession()

  const currentUserId = user?.id
  const oppositeUser = conversation?.oppositeUser

  const { inboxMessageCards } = useBrazeInboxMessageCards()
  const brazeConversationCard = useMemo(
    () => inboxMessageCards?.find(card => card.id === conversationId),
    [conversationId, inboxMessageCards],
  )

  const isCurrentAuthor = (message: ConversationTypes.ConversationMessage) => {
    if (message.entityType !== ConversationMessageType.Message) {
      return false
    }

    const entity = message.message as ConversationTypes.Message

    const isPeekingUser =
      entity.userId !== currentUserId?.toString() && oppositeUser?.id !== String(entity?.userId)

    if (isPeekingUser) {
      return String(entity?.userId) !== oppositeUser?.id
    }

    return entity?.userId === currentUserId?.toString()
  }

  if (brazeConversationCard) {
    return (
      <BrazeInboxMessage
        prefix={<MessageTime time={brazeConversationCard.updated.toISOString()} />}
        card={brazeConversationCard}
      />
    )
  }

  if (!conversationMessages.length) {
    return null
  }

  return (
    <>
      {conversationMessages.map((message, index) => (
        <ConversationMessage
          key={index}
          {...message}
          transactionId={conversation?.transactionId}
          isGrouped={isMessageGrouped(message, conversationMessages?.[index - 1])}
          isLast={index + 1 === conversationMessages.length}
          isCurrentAuthor={isCurrentAuthor(message)}
          toggleSidebar={toggleSidebar}
          openOfferModal={openOfferModal}
          conversationId={conversation?.id}
        />
      ))}
    </>
  )
}

export default ConversationMessages
